import { useLayoutEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import { useLabelDragStore } from '../hooks/useLabelDragStore';
import { shouldCopyLabelItem } from '../shared/routeLabelDragEnd';

import { LabelDragSourceTracker } from './LabelDragTrackers';

import type {
  LabelItemListItemComponent,
  LabelItemRenderer,
  LabelSidebarItem,
  LabelSidebarSection,
} from '../types';

type LabelItemDragPreviewProps = {
  item: LabelSidebarItem;
  /** Section the drag started in; its kind decides whether the drop will copy. */
  section: LabelSidebarSection;
  ItemComponent?: LabelItemListItemComponent;
  renderItem?: LabelItemRenderer;
};

type DragIndicatorKind = 'favorite' | 'copy' | 'remove';

const INDICATOR_SIZE_PX = 44;

/**
 * Drag preview for an item row. Shows a centered heart when dropping into Favorites, a `clone`
 * badge for other copy drops, or a `circle-minus` badge when over the remove zone. Auto-section
 * sources never show remove (membership is virtual), and copy badges only appear over a droppable
 * main/secondary destination.
 */
export default function LabelItemDragPreview({
  item,
  section,
  ItemComponent,
  renderItem,
}: LabelItemDragPreviewProps) {
  'use memo';

  const previewRef = useRef<HTMLDivElement | null>(null);
  const setDragItemSize = useLabelDragStore((state) => state.setDragItemSize);
  const copyModifier = useLabelDragStore((state) => state.copyModifier);
  const destinationKind = useLabelDragStore((state) => state.destinationKind);
  const overRemoveZone = useLabelDragStore((state) => state.overRemoveZone);

  useLayoutEffect(() => {
    const node = previewRef.current;
    if (!node) {
      return;
    }
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDragItemSize(rect.width, rect.height);
    }
  }, [setDragItemSize]);

  const sourceKind = section.kind;
  const canRemove = overRemoveZone && sourceKind !== 'auto';
  const willCopy = shouldCopyLabelItem({
    sourceKind,
    destinationKind,
    copyModifierActive: copyModifier,
  });

  const indicator: DragIndicatorKind | null = canRemove
    ? 'remove'
    : destinationKind === 'secondary'
      ? 'favorite'
      : willCopy
        ? 'copy'
        : null;

  const indicatorSx = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: INDICATOR_SIZE_PX,
    height: INDICATOR_SIZE_PX,
    borderRadius: '50%',
    boxShadow: 3,
    pointerEvents: 'none' as const,
    backgroundColor: 'action.disabled',
    color: 'text.main',
  };

  return (
    <Paper ref={previewRef} className="label-item-drag-preview" sx={{ position: 'relative' }}>
      <LabelDragSourceTracker kind={sourceKind} />
      {renderItem ? renderItem({ item, section }) : null}
      {!renderItem && ItemComponent && item.row ? <ItemComponent item={item.row} /> : null}
      {indicator === 'favorite' ? (
        <Box className="favorite-badge" sx={indicatorSx}>
          <FaIconUvilo icon="heart" size="lg" />
        </Box>
      ) : null}
      {indicator === 'copy' ? (
        <Box className="clone-badge" sx={indicatorSx}>
          <FaIconUvilo icon="clone" size="lg" />
        </Box>
      ) : null}
      {indicator === 'remove' ? (
        <Box className="remove-badge" sx={indicatorSx}>
          <FaIconUvilo icon="circle-minus" size="lg" />
        </Box>
      ) : null}
    </Paper>
  );
}
